import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Button, Input, Label, LoadingState } from '@biznomi/ui-react'
import { login } from '@/lib/api/auth'
import { useAuth } from '@/lib/auth/store'
import { useOrg, applyBrandColor } from '@/lib/useOrg'
import { getSubdomain, saveOrgCode, clearOrgCode, hostDecidesOrg } from '@/lib/org'

/*
 * 옛 세계(DCMC·옛 DB)에 사는 회사 코드 (2026-09-07).
 *
 * DCMC 회사 코드 화면은 자기 세계에 없는 코드를 여기로 **자동으로** 넘긴다
 * (`apps/client/routes/org-select`). 그래서 `dcnc` 처럼 오타를 친 DCMC 직원도 여기로
 * 밀려온다. 그 사람 코드는 이쪽 DB 에도 없으니, 되돌아갈 길이 없으면 앱을 지웠다
 * 까는 수밖에 없다. 2,600명이 첫 설치 때 한 번씩 치는 자리라 오타는 반드시 난다.
 *
 * 여기서 `dcmc` 를 받으면 저쪽으로 돌려보낸다. 이 목록이 **되돌아오는 유일한 길**이다.
 *
 * 🔴 **비면 갇힌다. 위험은 느는 쪽이 아니라 지우는 쪽이다.**
 *   느는 일은 없다 — 옛 DB 는 스키마가 동결됐고 새 고객은 전부 이쪽으로 온다.
 *   지우고 싶어지는 날은 온다. DCMC 계약이 끝나거나 새 DB 로 옮기는 날이다.
 *   그때 이 줄을 지우면 **앱을 새로 안 깐 사람**이 갇힌다. 그 사람 앱은 여전히
 *   옛 화면을 열고, 거기서 오타를 치면 여기로 밀려오는데 돌아갈 길이 없다.
 *   "DCMC 가 옛 DB 를 안 쓰게 됐다" 는 그 사람에게는 해당되지 않는 조건이다.
 *   → 지우는 조건은 **옛 진입 화면(`apps/client`)이 아무에게도 안 열릴 때**다.
 *
 * ⚠ 조회가 아니라 목록으로 둔다. 조회로 하면 호스트마다 `/api` 가 어느 서버에
 *   닿는지가 갈려서 런타임 판단이 들어온다.
 */
const OLD_WORLD_CODES = ['dcmc']

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const setAuth = useAuth((s) => s.setAuth)

  const { data: org, isLoading: orgLoading, isError: orgFailed } = useOrg()
  const [orgCode, setOrgCode] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const notice = (location.state as { notice?: string } | null)?.notice
  const needsOrgCode = getSubdomain() === ''
  /*
   * 회사 코드를 한 번 저장하면 되돌아갈 길이 없었다. 오타를 치거나 다른 회사로
   * 가려면 브라우저 저장소를 지우는 수밖에 없었고, 없는 코드를 넣으면 "회사 정보를
   * 확인하는 중…" 에서 영영 멈췄다. 로그인 전에도 바꿀 수 있어야 한다.
   * 주소가 회사를 정하는 경우(dcmc.staging…)에는 바꿔 봐야 주소가 이기므로 감춘다.
   */
  const canChangeOrg = !needsOrgCode && !hostDecidesOrg()

  function changeOrg() {
    clearOrgCode()
    window.location.reload()
  }
  const loginType = org?.loginType ?? 'EMAIL'
  const isEmployeeId = loginType === 'EMPLOYEE_ID'

  /*
   * 조직 정보를 받기 전에는 폼을 그리지 않는다.
   *
   * loginType 이 이메일인지 사번인지는 조직마다 다르다. 기다리지 않고 그리면
   * 사번 로그인 조직(DCMC 등)에서 "이메일" 칸이 잠깐 보였다가 "사번" 으로 바뀐다.
   * 그 사이에 이메일을 치기 시작한 직원은 로그인에 실패하고 앱을 의심한다
   * (운영 버그로그 [8] 과 같은 종류).
   *
   * 조회에 실패하면 그때는 그려준다 — 서버가 죽었다고 로그인 자체를 막을 수는 없다.
   */
  const formReady = !needsOrgCode && (!orgLoading || orgFailed)

  useEffect(() => applyBrandColor(org?.primaryColor), [org?.primaryColor])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const res = await login(identifier.trim(), password, loginType)
      if (!res.success || !res.data) {
        setError(res.error ?? '로그인에 실패했습니다')
        return
      }
      const { accessToken, refreshToken, user } = res.data
      setAuth({ accessToken, refreshToken }, { ...user, userId: user.userId ?? user.id ?? '' })

      const from = (location.state as { from?: string } | null)?.from
      navigate(from && !from.startsWith('/login') ? from : '/', { replace: true })
    } catch {
      setError('네트워크 오류입니다. 잠시 후 다시 시도해 주세요')
    } finally {
      setBusy(false)
    }
  }

  // 서브도메인이 없는 환경(앱·IP 접속) — 회사 코드를 먼저 받는다
  if (needsOrgCode) {
    return (
      /*
       * 🔴 DCMC 화면(`apps/client/routes/org-select`)과 **같은 모양으로 맞춘다**
       *   (2026-09-07). 경로 분리 뒤에는 사용자가 두 화면을 **연달아** 본다 —
       *   app.biznomi.com 에서 회사 코드를 넣고, 표준 고객이면 /mtt/ 로 넘어와
       *   여기를 다시 본다. 생김새가 다르면 그 자리에서 이음매가 드러난다.
       *
       *   맞춘 것: 로고 · "안녕하세요" · 부제 · 라벨 있는 입력칸 · 건물 아이콘 ·
       *            힌트 문구 · powered by 푸터 · 480px 한 칸 레이아웃.
       *   토큰은 packages/ui/styles/tokens.css 로 공유되므로 색은 저절로 맞는다.
       *
       * ⚠ 저쪽을 고치면 여기도 같이 봐야 한다. 두 파일이 같은 화면을 그린다.
       */
      <main
        className="mx-auto flex min-h-dvh max-w-[480px] flex-col items-center justify-center px-7 py-10"
        style={{ background: 'var(--color-card)' }}
      >
        {/* base 를 따라간다. `/mtt/` 를 박으면 base 가 바뀔 때 조용히 깨진다 */}
        <img src={`${import.meta.env.BASE_URL}logo/logo-dark.png`} alt="BizNomi" className="mb-10 h-12 w-auto" />

        <div className="text-[25px] font-extrabold">안녕하세요</div>
        <div className="mb-8 mt-1.5 text-[15px] text-[var(--color-text-secondary)]">
          회사 코드를 입력해주세요
        </div>

        <form
          className="w-full"
          onSubmit={(e) => {
            e.preventDefault()
            const code = orgCode.trim().toLowerCase()
            if (!code) return
            if (OLD_WORLD_CODES.includes(code)) {
              /*
               * 되돌려 보낸다. 코드도 같이 실어야 저기서 또 치지 않는다.
               *
               * 🔴 **이 줄은 `/org-select` 를 옛 앱이 받는다는 데 기대고 있다** (2026-09-07).
               *   보장하는 건 코드가 아니라 LB 다. prod url-map 의 `tenant-client`
               *   (`*.biznomi.com`) 기본값이 `bs-biznomi-client`(Svelte)라서, `/mtt` 로
               *   시작하지 않는 경로는 전부 옛 앱으로 간다. `/mtt` 쪽이 예외 규칙이다.
               *
               *   기대가 깨지는 날 = 그 기본값이 Svelte 가 아니게 되는 날. 그날은
               *   `/` · `/login` · `/records` 까지 같이 죽어서 2,600명이 몇 분 안에 안다.
               *   그래서 여기만 지키는 LB 규칙은 넣지 않았다 — 그날 이 링크만 살아 봐야
               *   갈 곳이 없고, 동결된 세계의 라우팅을 건드리는 값을 못 한다.
               *
               * ⚠ staging 에서는 이 길이 **안 돈다.** `app.staging` 기본값이 정적 서버라
               *   `apps/web/server.js` 의 catch-all 이 `/org-select` 를 잡아
               *   `/mtt/org-select` 로 돌려보낸다. 고장이 아니라 지형 차이다.
               *   staging 에는 한 호스트에 두 세계가 있는 자리가 없다.
               */
              window.location.href = `/org-select?org=${encodeURIComponent(code)}`
              return
            }
            saveOrgCode(code)
            window.location.reload()
          }}
        >
          <label className="mb-1.5 block text-xs font-semibold text-[var(--color-text-secondary)]">
            회사 코드
          </label>
          <div className="relative mb-3">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                <path d="M10 6h4" /><path d="M10 10h4" /><path d="M10 14h4" /><path d="M10 18h4" />
              </svg>
            </span>
            <Input
              value={orgCode}
              onChange={(e) => setOrgCode(e.target.value)}
              placeholder="회사 코드를 입력해주세요"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="pl-11"
            />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={!orgCode.trim()}>
            확인
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-[var(--color-text-muted)]">
          회사 관리자에게 코드를 문의하세요
        </p>

        <div className="mt-auto pt-8 text-[11px] text-[var(--color-text-muted)]">
          powered by <strong className="text-[var(--color-text-secondary)]">Biznomi</strong>
        </div>
      </main>
    )
  }

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        <div className="mb-2 text-center">
          {org?.logoUrl && <img src={org.logoUrl} alt="" className="mx-auto mb-3 h-12 object-contain" />}
          <h1 className="text-xl font-bold">{org?.name ?? (formReady ? '비즈노미' : '\u00a0')}</h1>
        </div>

        {!formReady && <LoadingState label="회사 정보를 확인하는 중…" />}

        {/*
         * 없는 코드를 넣으면 조회가 실패한다. 그때 "로그인 실패" 만 반복되면 원인이
         * 코드에 있다는 걸 알 수 없다. 코드를 되짚어 주고 바로 바꾸게 한다
         */}
        {orgFailed && canChangeOrg && (
          <div className="rounded-lg bg-[var(--color-warning-bg)] px-3 py-2.5 text-sm text-[var(--color-warning)]">
            <p>
              회사 코드 <b>{getSubdomain()}</b> 로 회사를 찾지 못했습니다.
            </p>
            <button type="button" onClick={changeOrg} className="mt-1 font-semibold underline">
              다른 회사 코드로 다시 시도
            </button>
          </div>
        )}

        {/* 비번을 바꾸고 돌아온 경우 등 — 왜 다시 로그인하라는지 알려준다 */}
        {notice && (
          <p className="rounded-lg bg-[var(--color-success-bg)] px-3 py-2 text-center text-sm text-[var(--color-success)]">
            {notice}
          </p>
        )}

        {formReady && (
          <>
        <div className="space-y-1.5">
          <Label htmlFor="identifier">{isEmployeeId ? '사번' : '이메일'}</Label>
          <Input
            id="identifier"
            type={isEmployeeId ? 'text' : 'email'}
            inputMode={isEmployeeId ? 'text' : 'email'}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">비밀번호</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" loading={busy} className="w-full">
          로그인
        </Button>

        {/* 로그인 전에도 회사를 바꿀 수 있어야 한다. 설정 화면은 로그인해야 들어간다 */}
        {canChangeOrg && !orgFailed && (
          <button
            type="button"
            onClick={changeOrg}
            className="block w-full py-1 text-center text-sm text-slate-400 underline"
          >
            다른 회사로 접속
          </button>
        )}
          </>
        )}
      </form>
    </main>
  )
}
